/**
 * Business dates (ARCH-15, UC-AGG-05; CF-14): "today", the end of a lot's expiry
 * day, the correction and alert windows and the default filters are calendar dates
 * in Europe/Chisinau, while technical moments stay UTC instants
 * (docs/shape-ops-business-date.md D1). From local midnight until 02:00 in winter
 * and 03:00 in summer, the UTC date is still the day before.
 */
export const BUSINESS_TIME_ZONE = 'Europe/Chisinau';

/**
 * The same date in SQL, whatever the session's TimeZone. `now()` is the
 * transaction's start, as for CURRENT_DATE, and the expression is STABLE like it.
 */
export const BUSINESS_DATE_SQL = `(now() AT TIME ZONE '${BUSINESS_TIME_ZONE}')::date`;

/**
 * Saturday and Sunday as ISO weekdays (1 Monday … 7 Sunday, PostgreSQL's ISODOW): the
 * non-working days the missing-report rule excepts in its initial configuration
 * (UC-ALRT-13, CF-56; docs/shape-ops-no-report-exceptions.md Decision 2).
 */
export const NON_WORKING_WEEKDAYS: readonly number[] = [6, 7];

// The DST rule comes from the IANA database, the one PostgreSQL's AT TIME ZONE
// reads. The parts are assembled here: a formatted en-CA string is locale data,
// and its pattern has changed between ICU releases.
const DATE_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: BUSINESS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The calendar date in Europe/Chisinau at the instant `now`, `YYYY-MM-DD`. */
export function businessDate(now: Date = new Date()): string {
  const parts = DATE_PARTS.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** A `YYYY-MM-DD` date moved by whole calendar days. */
export function addCalendarDays(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

/**
 * Whole calendar days in Europe/Chisinau from the date of `from` to the date of
 * `to`. Subtracted as UTC midnights, so a 23- or 25-hour day at a DST switch
 * still counts as one.
 */
export function calendarDaysBetween(from: Date, to: Date = new Date()): number {
  return (Date.parse(businessDate(to)) - Date.parse(businessDate(from))) / 86_400_000;
}
